import {useTranslationContext} from 'platform/components';

import {useCallback, useState} from 'react';

import {isNilOrEmpty} from 'ramda-adjunct';

import {isNotNilOrEmpty, useBoolean} from 'shared/web';

import {useHttpCalls} from '../api/useHttpCalls';
import {selectChoiceFilterOptions, selectGridCode} from '../store/createEagGridStore';
import {useEagGridSelector} from '../store/useEagGridSelector';
import {useEagGridStore} from '../store/useEagGridStore';
import type {ChoiceFilterOptions} from '../types/Api';

const EMPTY_OPTIONS: ChoiceFilterOptions = [];

type UseLazyChoiceFilterOptionsProps = {
  columnKey: string;
};

/**
 * Lazily loads choice filter options and caches them in EagGrid store state.
 * Cached options survive panel close/reopen — no redundant network requests.
 *
 * `loadOptions` captures `http`, `columnKey` and `t` directly. It is only ever
 * invoked via `useOnMount` (see ChoiceFilter/ChipsFilter/CheckboxFilter) — never
 * in an effect dependency array — so a stable identity is not required, and the
 * callback may re-create freely when its dependencies change.
 */
export function useLazyChoiceFilterOptions(props: UseLazyChoiceFilterOptionsProps) {
  const store = useEagGridStore();
  const cachedOptions = useEagGridSelector(selectChoiceFilterOptions(props.columnKey));
  const http = useHttpCalls();
  const t = useTranslationContext();

  const [isLoading, startLoading, stopLoading] = useBoolean();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const columnKey = props.columnKey;

  const loadOptions = useCallback(() => {
    const gridCode = selectGridCode(store.getState());
    const cached = selectChoiceFilterOptions(columnKey)(store.getState());

    if (isNilOrEmpty(gridCode) || isNilOrEmpty(columnKey)) {
      return Promise.resolve(EMPTY_OPTIONS);
    }

    if (isNotNilOrEmpty(cached)) {
      setErrorMessage(null);
      stopLoading();
      return Promise.resolve(cached as ChoiceFilterOptions);
    }

    startLoading();
    setErrorMessage(null);

    return http
      .getColumnFilterOptions(columnKey)
      .then((response) => {
        const options = response ?? EMPTY_OPTIONS;
        setErrorMessage(null);
        store.setChoiceFilterOptions(columnKey, options);
      })
      .catch(() => {
        setErrorMessage(t('general.errors.cannotLoadOptions'));
      })
      .finally(() => {
        stopLoading();
      });
  }, [store, startLoading, stopLoading, http, columnKey, t]);

  return {
    options: cachedOptions ?? EMPTY_OPTIONS,
    isLoading,
    errorMessage,
    loadOptions,
  };
}
